import { motion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const AboutBook = () => (
  <section
    id="about"
    data-testid="about-section"
    className="relative overflow-hidden bg-[#F6F5F0] py-20 sm:py-28"
  >
    <div className="grain pointer-events-none absolute inset-0 opacity-[0.4]" />
    <div className="pointer-events-none absolute -bottom-32 -right-32 h-[420px] w-[420px] rounded-full bg-[#20C9C3]/10 blur-[100px]" />

    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.85, ease: EASE }}
        >
          <div className="mb-5 flex items-center gap-3">
            <span className="h-px w-10 bg-[#C5A253]" />
            <span className="text-[12px] font-bold uppercase tracking-[0.24em] text-[#14A8A3]">
              About the Book
            </span>
          </div>
          <h2
            data-testid="about-heading"
            className="font-display text-3xl sm:text-4xl font-bold leading-[1.15] tracking-tight text-[#1E2A2B]"
          >
            You Don&rsquo;t Have to Be a Bookkeeper to Know When{" "}
            <span className="italic text-[#14A8A3]">Something Looks Wrong</span>
          </h2>
          <div className="mt-7 space-y-5 text-base md:text-lg leading-relaxed text-[#1E2A2B]/70">
            <p>
              Business owners make decisions using the numbers in their books,
              but many do not know what to look for when those numbers do not
              make sense.
            </p>
            <p className="font-display text-xl italic text-[#1E2A2B]">
              50 Bookkeeping Red Flags Every Business Owner Should Know gives
              you a place to start.
            </p>
            <p>
              Each red flag walks through what you might notice, why it
              matters, questions to ask, common causes and practical action
              steps.
            </p>
            <p>
              The purpose is not to turn you into a bookkeeper. It is to help
              you recognize when something in your books deserves a closer
              look.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 44 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="relative"
        >
          <div className="absolute -inset-4 rounded-[24px] border border-[#20C9C3]/25" />
          <div
            data-testid="about-card"
            className="relative overflow-hidden rounded-[20px] bg-white p-9 sm:p-12 shadow-[0_35px_70px_-25px_rgba(30,42,43,0.25)] border-t-4 border-t-[#20C9C3]"
          >
            <svg
              className="pointer-events-none absolute bottom-0 right-0 w-2/3 text-[#C5A253]/20"
              viewBox="0 0 400 160"
              fill="none"
              preserveAspectRatio="none"
            >
              {[0, 1, 2, 3, 4].map((i) => (
                <path
                  key={i}
                  d={`M0 ${40 + i * 26} C 140 ${i * 26}, 260 ${80 + i * 26}, 400 ${30 + i * 26}`}
                  stroke="currentColor"
                  strokeWidth="1"
                />
              ))}
            </svg>
            <div className="relative">
              <span
                data-testid="about-card-number"
                className="font-display block text-8xl sm:text-9xl font-black leading-none text-[#20C9C3]"
              >
                50
              </span>
              <span className="mt-4 block h-[3px] w-16 bg-[#C5A253]" />
              <h3 className="mt-6 font-display text-2xl sm:text-3xl font-bold text-[#1E2A2B]">
                Red Flags to Look For
              </h3>
              <p className="mt-4 text-base leading-relaxed text-[#1E2A2B]/65">
                From unreconciled accounts and negative balances to duplicate
                income, old transactions and numbers that simply do not make
                sense, the book helps you identify bookkeeping issues before
                they become bigger problems.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
